// SplitButton.jsx
// Client: vmfive
// Version: 1.1.0
// Orange primary button with a caret that opens a popover menu.
// Left part (.split__main) fires the primary action; right part (.split__caret)
// opens the dropdown.
//
// B-1 fix: popover is rendered via ReactDOM.createPortal into document.body
// with position:fixed + JS-computed coordinates — safe inside overflow:hidden
// and CSS-transform containers.  Scroll/resize events close the popover.

import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Icon from './Icon';
import useDismiss from './useDismiss';
import './components-company-application.css';

/**
 * SplitButton — props:
 *   label     {string}    Label shown on the main button
 *   icon      {string=}   Lucide icon name shown left of label (default: 'plus')
 *   onMain    {Function}  Called when the main button is clicked
 *   menu      {Array}     Dropdown items: [{ label, icon?, onClick, danger? }]
 *   className {string=}   Extra CSS class on the wrapper span
 */
export default function SplitButton({
  label,
  icon = 'plus',
  onMain,
  menu = [],
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [popPos, setPopPos] = useState({ top: 0, right: 0 });

  const close = useCallback(() => setOpen(false), []);
  const [triggerRef, popRef] = useDismiss(open, close);

  // We compute position from the caret button specifically
  const caretRef = useRef(null);

  const computePosition = useCallback(() => {
    // Fall back to the wrapper ref if caret ref is unavailable
    const el = caretRef.current || triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPopPos({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
  }, [triggerRef]);

  const handleCaretClick = () => {
    if (!open) {
      computePosition();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  // Close on scroll or resize
  useEffect(() => {
    if (!open) return;
    const handleClose = () => setOpen(false);
    window.addEventListener('scroll', handleClose, { capture: true, passive: true });
    window.addEventListener('resize', handleClose, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleClose, { capture: true });
      window.removeEventListener('resize', handleClose);
    };
  }, [open]);

  const popover = open
    ? ReactDOM.createPortal(
        <div
          ref={popRef}
          className="pop pop--fixed"
          role="menu"
          style={{ top: popPos.top, right: popPos.right }}
        >
          {menu.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={`pop__item${item.danger ? ' danger' : ''}`}
              onClick={() => {
                setOpen(false);
                if (item.onClick) item.onClick();
              }}
            >
              {item.icon && (
                <Icon name={item.icon} size={15} strokeWidth={1.8} />
              )}
              {item.label}
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <span
        ref={triggerRef}
        className={`split ${className}`}
        style={{ position: 'relative', display: 'inline-flex' }}
      >
        <button
          type="button"
          className="split__main"
          onClick={onMain}
        >
          <Icon name={icon} size={15} strokeWidth={1.8} />
          {label}
        </button>

        <button
          ref={caretRef}
          type="button"
          className="split__caret"
          aria-label="More actions"
          aria-expanded={open ? 'true' : 'false'}
          aria-haspopup="menu"
          onClick={handleCaretClick}
        >
          <Icon name="chevron-down" size={14} strokeWidth={1.8} />
        </button>
      </span>
      {popover}
    </>
  );
}
